import { Fragment, useContext, useEffect, useState } from 'react';
import { addInbound, deleteInbound, updateInbound, sourceOptions, inboundTypeOptions, inboundGeometryOptions } from '../services/inboundService';
import { Button } from 'react-bootstrap'
import ProtectedComponent from './common/ProtectedComponent';
import { AuthContext } from './Login/AuthContext';
import Icons from './common/Icons';

import { DatabaseContext } from "./DatabaseContext"
import { getPrettyBkgColor, dateYYYYMMMDD, noCheck, validatePositiveInt } from '../services/commonParse';
import TableViewAdd, { defaultAdd, defaultResetAdd } from './common/TableViewAdd';
import TableViewEdit, { defaultReset, defaultEdit, defaultValidateEdit } from './common/TableViewEdit';
import { HGCFormRowText, HGCFormRowDate, HGCFormRowSelect } from './common/SimpleForm';
import PaginateTable, { CategoryFilter, PassFailFilter } from './common/TableDisplay';
import { findTableRow } from '../services/tableUtils';

const InboundColumns = () => [
  { Header: 'From', Cell: InboundCellID, Filter: CategoryFilter((inbound) => inbound.source), CellStyle: arrivedStyler },
  { Header: 'Item Type', Cell: InboundCellType, Filter: CategoryFilter(InboundTypeCat), CellStyle: arrivedStyler },
  { Header: 'Quantity', Cell: (inbound) => inbound.quantity, Filter: PassFailFilter((inbound) => inbound.act_date === null, "Hide Arrived", false), CellStyle: arrivedStyler },
  { Header: 'Link', Cell: (inbound) => inbound.link, CellStyle: arrivedStyler },
  { Header: 'Note', Cell: (inbound) => inbound.comment, CellStyle: arrivedStyler },
]

const arrivedStyler = (inbound) => {
  return {
    backgroundColor: getPrettyBkgColor(inbound.act_date !== null ? "green" : null),
  }
}

const InboundCellID = (inbound) => {
  const { user } = useContext(AuthContext);
  const [showEdit, setShowEdit] = useState(false);
  const { updateInboundList } = useContext(DatabaseContext);
  const removeAction = async () => {
    await deleteInbound(inbound.id);
    updateInboundList();
  }

  const source_str = sourceOptions.find((x) => x.value === inbound.source).label

  return <Fragment>
    <b>{source_str}</b>
    <Button variant='light' size='sm' onClick={() => { setShowEdit(true) }}>{Icons.edit}</Button>
    {user.role === "admin" && <Button variant='light' size='sm' onClick={removeAction}>{Icons.remove}</Button>}
    <br />
    Est arrival date: {dateYYYYMMMDD(inbound.est_date)}<br />
    Recieved date: {dateYYYYMMMDD(inbound.act_date)}<br />
    {showEdit && <InboundEdit id={inbound.id} showState={[showEdit, setShowEdit]} />}
  </Fragment>
}

const InboundEdit = ({ id, showState }) => {
  const [, setShow] = showState;
  const { inboundList, updateInboundList } = useContext(DatabaseContext);
  const origData = findTableRow(inboundList, (x) => x.id === id);
  // Creating all items related to the display form
  const form = {
    'source': useState(origData.source),
    'type': useState(origData.type),
    "geometry": useState(origData.geometry),
    "est_date": useState(origData.est_date),
    "act_date": useState(origData.act_date),
    "quantity": useState(origData.quantity),
    "link": useState(origData.link),
    "comment": useState(origData.comment),
  }
  // eslint-disable-next-line
  const errorForm = {
    "source": useState(""),
    "type": useState(""),
    "geometry": useState(""),
    "quantity": useState(""),
    "link": useState(""),
    "comment": useState("")
  };
  const [invalid, setInvalid] = useState("")

  const resetAction = defaultReset(form, origData)
  const editAction = defaultEdit(id, form, updateInbound, updateInboundList, setShow)
  useEffect(() => { setInvalid(defaultValidateEdit(errorForm)) }, [errorForm])

  return <TableViewEdit title={`Edit Inbound order [${id}][${origData.id}]`} showState={showState} resetAction={resetAction} editAction={editAction} invalid={invalid}>
    <HGCFormRowSelect label="FROM" dataState={form["source"]} errorState={errorForm["source"]} options={sourceOptions} allowNull={false} />
    <HGCFormRowSelect label="ITEM TYPE" dataState={form["type"]} errorState={errorForm["type"]} options={inboundTypeOptions} allowNull={false} />
    <HGCFormRowSelect label="ITEM GEOMETRY" dataState={form["geometry"]} errorState={errorForm["geometry"]} options={inboundGeometryOptions} allowNull={false} />
    <HGCFormRowDate label="EST. ARRIVAL" dataState={form["est_date"]} />
    <HGCFormRowDate label="RECIEVED DATE" dataState={form["act_date"]} />
    <HGCFormRowText label="QUANTITY" dataState={form["quantity"]} errorState={errorForm["quantity"]} validate={validatePositiveInt(true)} />
    <HGCFormRowText label="LINK" dataState={form["link"]} errorState={errorForm["link"]} validate={noCheck} />
    <HGCFormRowText label="NOTES" dataState={form["comment"]} errorState={errorForm["comment"]} validate={noCheck} />
  </TableViewEdit>
}

const InboundAdd = ({ showState }) => {
  const { updateInboundList } = useContext(DatabaseContext);
  const [, setShow] = showState;
  const form = {
    'source': useState(null),
    'type': useState(null),
    'geometry': useState(null),
    "est_date": useState(new Date()),
    "quantity": useState(0),
    "link": useState(""),
    "comment": useState(""),
  }
  // eslint-disable-next-line
  const errorForm = {
    "source": useState(""),
    "type": useState(""),
    "geometry": useState(""),
    "quantity": useState(""),
    "link": useState(""),
    "comment": useState("")
  };

  const [invalid, setInvalid] = useState("");
  const resetAction = defaultResetAdd(form, { "est_date": () => new Date(), quantity: 0 })
  const addAction = defaultAdd(form, addInbound, updateInboundList, resetAction, setShow);
  useEffect(() => { setInvalid(defaultValidateEdit(errorForm)) }, [errorForm])

  return <TableViewAdd title="Add new Inbound item" showState={showState} addAction={addAction} invalid={invalid}>
    <HGCFormRowSelect label="FROM" dataState={form["source"]} errorState={errorForm["source"]} options={sourceOptions} allowNull={false} />
    <HGCFormRowSelect label="ITEM TYPE" dataState={form["type"]} errorState={errorForm["type"]} options={inboundTypeOptions} allowNull={false} />
    <HGCFormRowSelect label="ITEM GEOMETRY" dataState={form["geometry"]} errorState={errorForm["geometry"]} options={inboundGeometryOptions} allowNull={false} />
    <HGCFormRowDate label="EST. ARRIVAL" dataState={form["est_date"]} />
    <HGCFormRowText label="QUANTITY" dataState={form["quantity"]} errorState={errorForm["quantity"]} validate={validatePositiveInt(true)} />
    <HGCFormRowText label="LINK" dataState={form["link"]} errorState={errorForm["link"]} validate={noCheck} />
    <HGCFormRowText label="NOTES" dataState={form["comment"]} errorState={errorForm["comment"]} validate={noCheck} />
  </TableViewAdd>
}

const InboundCellType = (inbound) => {
  return `${inbound.type}/${inbound.geometry}`
}

const InboundTypeCat = (inbound) => {
  return `${inbound.type}/${inbound.geometry}`
}

export const InboundList = () => {
  const { inboundList } = useContext(DatabaseContext);
  const [showAdd, setShowAdd] = useState(false);

  return <ProtectedComponent>
    <Button onClick={() => { setShowAdd(true) }}>Add inbound order</Button>
    <InboundAdd showState={[showAdd, setShowAdd]} />
    <hr />
    <PaginateTable tableData={inboundList} columnLayout={InboundColumns()} entriesPerPage={10} />
  </ProtectedComponent>
}

export default InboundList;
